/**
 * Wall-clock time in a chosen IANA time zone (TRUS-695).
 *
 * The user's selected zone (Settings > Preferences) is the single source for
 * entering and showing calendar / meeting times; if they have none, the org's
 * zone, then the browser's. Inputs (`<input type="date|time|datetime-local">`)
 * hold wall-clock time in that zone; the API always receives an ISO string WITH
 * its offset (UTC), never a bare `2026-10-21T09:00`, which the server would have
 * to guess at.
 */
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

/** True when `tz` is an IANA zone this runtime knows. */
export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** This device's IANA zone ('UTC' when unknown). */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** The user's zone, else the org's, else the browser's. */
export function resolveTimeZone(userTz?: string | null, orgTz?: string | null): string {
  if (isValidTimeZone(userTz)) return userTz;
  if (isValidTimeZone(orgTz)) return orgTz;
  return browserTimeZone();
}

function toMs(value: string | number | Date): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  // A server timestamp without an offset is UTC.
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTz ? value : `${value}Z`).getTime();
}

/** `YYYY-MM-DDTHH:mm` (for datetime-local) of an instant, in `tz`. */
export function toZonedInput(value: string | number | Date, tz: string): string {
  const ms = toMs(value);
  if (Number.isNaN(ms)) return '';
  return formatInTimeZone(ms, tz, "yyyy-MM-dd'T'HH:mm");
}

/** `{date: YYYY-MM-DD, time: HH:mm}` (for date + time inputs) of an instant, in `tz`. */
export function toZonedInputs(value: string | number | Date, tz: string): { date: string; time: string } {
  const v = toZonedInput(value, tz);
  const [date = '', time = ''] = v.split('T');
  return { date, time };
}

/**
 * Wall-clock `YYYY-MM-DDTHH:mm` (or a date + `HH:mm`) in `tz` -> epoch ms, or
 * null when it is not a valid date/time. 9:00 in America/Los_Angeles is 16:00Z
 * in summer (PDT) and 17:00Z in winter (PST).
 */
export function zonedInputToMs(local: string, tz: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(local || '');
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  // Reject impossible dates (2026-02-31) instead of rolling them over.
  const probe = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  if (probe.getUTCMonth() !== Number(mo) - 1 || probe.getUTCDate() !== Number(d)) return null;
  const ms = fromZonedTime(`${y}-${mo}-${d}T${h}:${mi}:00`, tz).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Wall-clock input in `tz` -> UTC ISO string (with `Z`), or null. */
export function zonedInputToIso(local: string, tz: string): string | null {
  const ms = zonedInputToMs(local, tz);
  return ms == null ? null : new Date(ms).toISOString();
}

/** Date + time inputs in `tz` -> epoch ms, or null. */
export function zonedInputsToMs(date: string, time: string, tz: string): number | null {
  if (!/^\d{2}:\d{2}$/.test(time || '')) return null;
  return zonedInputToMs(`${date}T${time}`, tz);
}

/**
 * A Date whose LOCAL fields (getHours, getDate, ...) are the wall-clock time of
 * the instant in `tz` - for bucketing events into calendar-grid days / hours,
 * which date-fns compares in the browser's local fields.
 */
export function toZonedWallDate(value: string | number | Date, tz: string): Date {
  return toZonedTime(toMs(value), tz);
}

/** Today's wall-clock "now" in `tz` (as a Date with local fields = that time). */
export function zonedNow(tz: string): Date {
  return toZonedTime(Date.now(), tz);
}

/** The zone's short name at that instant ("PDT", "PST", "GMT+5:30"). */
export function zoneAbbreviation(value: string | number | Date, tz: string, locale?: string): string {
  const ms = toMs(value);
  try {
    const parts = new Intl.DateTimeFormat(locale || 'en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(
      new Date(Number.isNaN(ms) ? Date.now() : ms),
    );
    return parts.find((p) => p.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
}

// ---------------------------------------------------------------------------
// TRUS-697: an item's own zone
// ---------------------------------------------------------------------------
//
// A time-bearing item (event, meeting, task due time, follow-up, scheduled
// message) is stored as UTC plus the IANA zone it was scheduled in - the
// creator's by default, or the one picked beside the date/time field. Times are
// shown in the VIEWER's zone; when the item's zone differs, its own time is
// shown too: "9:00 AM PDT (12:00 PM EDT event time)".

function offsetMinutes(ms: number, tz: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(ms));
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    return Math.round((asUtc - Math.floor(ms / 60_000) * 60_000) / 60_000);
  } catch {
    return null;
  }
}

/**
 * True when the item's wall-clock time differs from the viewer's at that
 * instant. Two names with the same offset (America/Vancouver and
 * America/Los_Angeles) do not differ; an unknown item zone never does.
 */
export function zonesDiffer(value: string | number | Date, viewerTz: string, itemTz?: string | null): boolean {
  if (!isValidTimeZone(itemTz) || !isValidTimeZone(viewerTz) || itemTz === viewerTz) return false;
  const ms = toMs(value);
  if (Number.isNaN(ms)) return false;
  const a = offsetMinutes(ms, viewerTz);
  const b = offsetMinutes(ms, itemTz);
  return a != null && b != null && a !== b;
}

/** "9:00 AM PDT" - the clock time of an instant in `tz`, with its abbreviation. */
export function formatClock(value: string | number | Date, tz: string, locale?: string): string {
  const ms = toMs(value);
  try {
    return new Intl.DateTimeFormat(locale || 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: tz,
    }).format(new Date(ms));
  } catch {
    return '';
  }
}

/**
 * The item's own clock time ("12:00 PM EDT") when its zone differs from the
 * viewer's, else null. Callers wrap it: t('time.itemZoneNote', '({{time}} event time)').
 */
export function itemZoneTime(
  value: string | number | Date,
  viewerTz: string,
  itemTz?: string | null,
  locale?: string,
): string | null {
  if (!zonesDiffer(value, viewerTz, itemTz)) return null;
  return formatClock(value, itemTz as string, locale) || null;
}

/** Every IANA zone this runtime knows (a short list on runtimes without Intl.supportedValuesOf). */
export function allTimeZones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    const list = fn ? fn('timeZone') : [];
    if (list.length) return list.includes('UTC') ? list : [...list, 'UTC'];
  } catch {
    /* fall through */
  }
  return [
    'America/Los_Angeles',
    'America/Denver',
    'America/Phoenix',
    'America/Chicago',
    'America/New_York',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Puerto_Rico',
    'America/Mexico_City',
    'Europe/London',
    'Europe/Madrid',
    'Asia/Kolkata',
    'Asia/Manila',
    'UTC',
  ];
}
